import { useEffect } from 'react'
import { useApp } from '@/context/AppContext'
import { loadTribeData } from '@/services/aggregatedDataLoader'
import type { Question, ResponseDistribution } from '@/types'

/**
 * Hook that loads tribe-specific data when the selected tribe changes.
 * Merges loaded data into the app state.
 */
export function useTribeDataLoader() {
  const { state, dispatch } = useApp()
  const { filters, data } = state
  const selectedTribe = filters.selectedTribe

  useEffect(() => {
    if (!data || selectedTribe === 'total') {
      return
    }

    async function loadTribe() {
      if (!data) return // TypeScript null check

      try {
        const tribeData = await loadTribeData(selectedTribe)

        if (Object.keys(tribeData).length === 0) {
          return // No data or already cached
        }

        // Merge tribe data into questions
        const updatedQuestions: Record<string, Question> = { ...data.questions }

        for (const [questionId, questionData] of Object.entries(tribeData)) {
          if (updatedQuestions[questionId]) {
            const filterKey = `tribe:${selectedTribe}|break:total`
            updatedQuestions[questionId] = {
              ...updatedQuestions[questionId],
              data: {
                ...updatedQuestions[questionId].data,
                [filterKey]: questionData as ResponseDistribution,
              },
            }
          }
        }

        // Update state with merged data - spread all required fields
        dispatch({
          type: 'SET_DATA',
          payload: {
            meta: data.meta,
            tribes: data.tribes,
            breaks: data.breaks,
            questions: updatedQuestions,
            demographics: data.demographics || {},
            dashboards: data.dashboards,
          },
        })
      } catch (error) {
        console.warn(`Failed to load tribe data for ${selectedTribe}:`, error)
      }
    }

    loadTribe()
  }, [selectedTribe, data, dispatch])
}
